import SEO from "../../components/SEO";
import { Link } from "react-router-dom";

export default function CompanyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "dot Blue株式会社",
    "url": siteUrl,
    "logo": "https://static.readdy.ai/image/9d74872511a6806a1ddbaf05c38ab433/5a08c197f8230028d9ecca8183376b65.png",
    "description": "生成AI・LLMを活用したDX支援企業。AI実装から運用まで伴走します。",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "JP"
    }
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "会社概要 - dot Blue",
    "description": "dot Blue株式会社の会社概要ページです。",
    "url": `${siteUrl}/company`,
    "publisher": {
      "@type": "Organization",
      "name": "dot Blue",
      "url": siteUrl
    }
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [webPageSchema, organizationSchema]
  };

  return (
    <>
      <SEO
        title="会社概要 - dot Blue"
        description="dot Blue株式会社の会社概要ページです。生成AI・LLMを活用したDX支援企業として、AI実装から運用まで伴走します。"
        keywords="dot Blue, 会社概要, AI企業, DX支援"
        ogImage={`${siteUrl}/og-image-company.jpg`}
        schema={combinedSchema}
      />

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <span className="text-xl font-bold text-gray-900">dot Blue</span>
              </Link>
              <Link
                to="/"
                className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                ホームに戻る
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-24 pb-20">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">会社概要</h1>

            <div className="prose prose-lg max-w-none">
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-teal-600 pb-2">会社情報</h2>
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <p className="text-gray-700 mb-2">
                      <strong className="text-gray-900 text-lg">会社名：</strong>
                    </p>
                    <p className="text-gray-700 pl-4">株式会社dot Blue</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <p className="text-gray-700 mb-2">
                      <strong className="text-gray-900 text-lg">代表者：</strong>
                    </p>
                    <p className="text-gray-700 pl-4">平舘慶大(代表取締役)</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <p className="text-gray-700 mb-2">
                      <strong className="text-gray-900 text-lg">設立：</strong>
                    </p>
                    <p className="text-gray-700 pl-4">2025年4月11日</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <p className="text-gray-700 mb-2">
                      <strong className="text-gray-900 text-lg">所在地：</strong>
                    </p>
                    <p className="text-gray-700 pl-4">東京都目黒区目黒二丁目11番3号</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <p className="text-gray-700 mb-2">
                      <strong className="text-gray-900 text-lg">資本金：</strong>
                    </p>
                    <p className="text-gray-700 pl-4">500万円</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <p className="text-gray-700 mb-2">
                      <strong className="text-gray-900 text-lg">お問い合わせ：</strong>
                    </p>
                    <p className="text-gray-700 pl-4">
                      <a href="mailto:contact@aidotblue.com" className="text-teal-600 hover:underline">
                        contact@aidotblue.com
                      </a>
                      <br />
                      (または
                      <Link to="/#contact" className="text-teal-600 hover:underline ml-1">
                        お問い合わせフォーム
                      </Link>
                      )
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold">dot Blue</span>
              </div>
              <div className="flex gap-6 text-sm">
                <Link to="/privacy-policy" className="hover:text-teal-400 transition-colors cursor-pointer">
                  プライバシーポリシー
                </Link>
                <Link to="/terms" className="hover:text-teal-400 transition-colors cursor-pointer">
                  利用規約
                </Link>
                <Link to="/security" className="hover:text-teal-400 transition-colors cursor-pointer">
                  セキュリティ
                </Link>
                <Link to="/company" className="hover:text-teal-400 transition-colors cursor-pointer">
                  会社概要
                </Link>
                <a href="mailto:contact@aidotblue.com" className="hover:text-teal-400 transition-colors cursor-pointer">
                  お問い合わせ
                </a>
              </div>
            </div>
            <div className="text-center text-sm text-gray-400 mt-6">
              Copyright © dot Blue Inc. All Rights Reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
